// hooks/useSupabaseAudio.ts - Version complète SANS nettoyage des préfixes
import { useState, useCallback, useRef, useEffect } from 'react'
import SupabaseAudioService from '../services/supabaseAudioService'

// Fonction utilitaire pour vérifier les AbortError
function isAbortError(error: unknown): error is DOMException {
  return error instanceof DOMException && error.name === 'AbortError'
}

export const useSupabaseAudio = (languageCode: string = 'wf') => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null)
  const [loading, setLoading] = useState(false)
  const [lastError, setLastError] = useState<string | null>(null)
  
  // REF pour éviter les re-créations multiples
  const currentWordRef = useRef<string | null>(null)
  const audioInstanceRef = useRef<HTMLAudioElement | null>(null)
  const playingPromiseRef = useRef<Promise<void> | null>(null)

  // Nettoyage à la destruction du composant
  useEffect(() => {
    return () => {
      if (audioInstanceRef.current) {
        try {
          audioInstanceRef.current.pause()
          audioInstanceRef.current.currentTime = 0
        } catch (e) {
}
        audioInstanceRef.current = null
      }
      if (playingPromiseRef.current) {
        playingPromiseRef.current = null
      }
      currentWordRef.current = null
    }
  }, [])

  // Mapping correct des codes de langue
  const getCorrectLanguageCode = (inputLanguageCode: string): string => {
    const languageMapping: { [key: string]: string } = {
      'la': 'li',  // Correction : 'la' → 'li' pour Lingala
      'li': 'li',  // Lingala
      'bm': 'bm',  // Bambara (pas de modification)
      'co': 'co',  // Copte
      'wf': 'wf',  // Wolof
      'sw':'sw',
      'yo':'yo',   // Yoruba
      'ff':'ff',
     
    }

    const correctedCode = languageMapping[inputLanguageCode] || inputLanguageCode
    
    if (correctedCode !== inputLanguageCode) {
    
    }
    
    return correctedCode
  }

  const playWord = useCallback(async (word: string): Promise<boolean> => {
    // GUARD: Éviter de jouer le même mot plusieurs fois
    if (currentWordRef.current === word && isPlaying) {
 
      return true
    }

    // Arrêter proprement l'audio précédent
    if (audioInstanceRef.current) {
      try {
        audioInstanceRef.current.pause()
        audioInstanceRef.current.currentTime = 0
      } catch (e) {
}
      audioInstanceRef.current = null
    }

    // Annuler la promesse de lecture précédente si elle existe
    if (playingPromiseRef.current) {
      playingPromiseRef.current = null
    }

    setLoading(true)
    setLastError(null)
    setIsPlaying(false)
    currentWordRef.current = word
    
    try {
      // Utiliser le code langue corrigé
      const correctLanguageCode = getCorrectLanguageCode(languageCode)
      
    

      // Recherche audio
      const audioUrl = await SupabaseAudioService.getWordAudioUrl(correctLanguageCode, word)
      
      if (!audioUrl) {
        setLastError(`Audio non disponible pour "${word}"`)
        setLoading(false)
        currentWordRef.current = null
        
        // Diagnostic en mode développement
        if (process.env.NODE_ENV === 'development') {
          await SupabaseAudioService.diagnosticSearch(correctLanguageCode, word)
        }
        
        return false
      }

      // Création de l'instance audio
    
      const audio = new Audio(audioUrl)
      audioInstanceRef.current = audio
      setCurrentAudio(audio)

      // Configuration des gestionnaires AVANT le play()
      const setupEventListeners = () => {
        if (!audio) return

        audio.onended = () => {
         
          setIsPlaying(false)
          setCurrentAudio(null)
          setLoading(false)
          currentWordRef.current = null
          audioInstanceRef.current = null
        }

        audio.onerror = (e) => {
setLastError(`Erreur de lecture pour "${word}"`)
          setIsPlaying(false)
          setCurrentAudio(null)
          setLoading(false)
          currentWordRef.current = null
          audioInstanceRef.current = null
        }

        audio.onloadeddata = () => {
        
        }

        audio.oncanplay = () => {
         
          setLoading(false)
        }
      }

      setupEventListeners()

      // Tentative de lecture avec gestion des promesses
      const playPromise = audio.play()
      playingPromiseRef.current = playPromise

      if (playPromise !== undefined) {
        try {
          await playPromise
        
          setIsPlaying(true)
          return true
        } catch (playError) {
          // Gestion sécurisée des erreurs TypeScript
          if (isAbortError(playError)) {
          
          } else {
setLastError(`Impossible de lire "${word}"`)
          }
          
          setIsPlaying(false)
          setCurrentAudio(null)
          setLoading(false)
          currentWordRef.current = null
          audioInstanceRef.current = null
          return false
        }
      }

      return true

    } catch (error) {
setLastError(`Erreur technique pour "${word}"`)
      setIsPlaying(false)
      setCurrentAudio(null)
      setLoading(false)
      currentWordRef.current = null
      audioInstanceRef.current = null
      return false
    }
  }, [languageCode])

  const stopAudio = useCallback(() => {
    if (audioInstanceRef.current) {
      try {
        audioInstanceRef.current.pause()
        audioInstanceRef.current.currentTime = 0
      } catch (e) {
}
      audioInstanceRef.current = null
    }
    
    setIsPlaying(false)
    setCurrentAudio(null)
    setLoading(false)
    setLastError(null)
    currentWordRef.current = null
    playingPromiseRef.current = null
  }, [])

  const clearError = useCallback(() => {
    setLastError(null)
  }, [])

  // Fonction de diagnostic (utile pour le développement)
  const diagnoseWord = useCallback(async (word: string) => {
    if (process.env.NODE_ENV === 'development') {
      const correctLanguageCode = getCorrectLanguageCode(languageCode)
      await SupabaseAudioService.diagnosticSearch(correctLanguageCode, word)
    }
  }, [languageCode])

  return { 
    playWord, 
    stopAudio,
    clearError,
    diagnoseWord,
    isPlaying, 
    loading,
    lastError
  }
}

// Hook utilitaire pour les composants qui ont besoin d'info sur l'état audio global
export const useAudioState = () => {
  const [globalAudioState, setGlobalAudioState] = useState({
    currentlyPlaying: null as string | null,
    isAnyPlaying: false
  })

  return {
    ...globalAudioState,
    setCurrentlyPlaying: (word: string | null) => {
      setGlobalAudioState({
        currentlyPlaying: word,
        isAnyPlaying: !!word
      })
    }
  }
}

export default useSupabaseAudio
